<script setup lang="ts">
import { ref, computed, type ComponentPublicInstance } from 'vue'
import { OnClickOutside } from '@vueuse/components'
import dayjs, { Dayjs } from 'dayjs'
import { usePrevioDS } from '../../../../../composables/helpers'
import { useFocusManager } from './composables/useFocusManager'
import { useFormatSegmentBuilder } from './composables/formatSegmentBuilder'
import { type IErrorState } from './model/ErrorState'

const model = defineModel<Dayjs | null>()
const errorState = defineModel<IErrorState>('errorState', { required: true })

type Emits = {
  (e: 'focus'): void
}

const props = defineProps({
  format: {
    type: String,
    default: undefined,
  },
  hideTimeField: {
    type: Boolean,
    default: false,
  },
  defaultTime: {
    type: String,
    validator: (value?: string): boolean => {
      if (!value) return true
      return dayjs(value, 'HH:mm', true).isValid()
    },
    required: false,
  },
  disableClearable: {
    type: Boolean,
    default: false,
  },
  selectable: {
    type: Boolean,
    default: false,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  minuteIncrement: {
    type: Number,
    default: undefined,
  },
})

const emits = defineEmits<Emits>()

const segmentBuilder = useFormatSegmentBuilder()
const focusManager = useFocusManager()
const ds = usePrevioDS()
const format = computed(() => {
  if (props.format) {
    return props.format
  }

  return props.hideTimeField
    ? ds.useFormat().dateTime.NICE_DATE
    : ds.useFormat().dateTime.NICE_DATE_TIME
})

// Get parsed format and segments
const tokens = computed(() => segmentBuilder.parseFormat(format.value))
const segments = computed(() =>
  tokens.value.map((token) => {
    const resolved = segmentBuilder.tokenToSegment(token, props.defaultTime, props.minuteIncrement)

    if (resolved === null) {
      return null
    }

    if ('component' in resolved) {
      return resolved
    }

    return { component: resolved, props: {} }
  }),
)

const segmentRefs = ref<any[]>([])

const setSegmentRef = (index: number) => {
  return (el: Element | ComponentPublicInstance | null): void => {
    segmentRefs.value[index] = el
  }
}

const isSelected = ref<boolean>(false)

const validateSegments = (): void => {
  if (!props.disableClearable) {
    return
  }

  errorState.value.clear()

  for (const ref of segmentRefs.value) {
    if (!('validate' in ref)) {
      continue
    }

    if (typeof ref.validate !== 'function') {
      continue
    }

    ref.validate(errorState.value)
  }
}

const focusInDirection = (from: number, step: number): void => {
  for (let i = from + step; i >= 0 && i < segmentRefs.value.length; i += step) {
    const ref = segmentRefs.value[i]

    if (ref?.focus instanceof Function) {
      ref.focus()
      break
    }
  }
}

const focusNext = (currentIndex: number): void => {
  focusInDirection(currentIndex, 1)
}

const focusPrev = (currentIndex: number): void => {
  focusInDirection(currentIndex, -1)
}

const deselect = (): void => {
  isSelected.value = false
}

const select = (): void => {
  isSelected.value = true
}

const onFocus = (): void => {
  if (!props.selectable || props.readonly) {
    return
  }

  select()
  emits('focus')
}

const onWrapperClick = onFocus

const onHandleClickOutside = (): void => {
  deselect()
  focusManager.unfocus()
  validateSegments()
}

defineExpose({
  select,
  deselect,
})
</script>

<template>
  <on-click-outside class="p-flex p-flex-center p-no-wrap" @trigger="onHandleClickOutside">
    <div
      class="ds-format-segment-builder__wrapper p-flex p-flex-center p-no-wrap"
      :class="{
        'ds-format-segment-builder__wrapper--selectable': selectable && !readonly,
        'ds-format-segment-builder__wrapper--selected': isSelected,
      }"
      @click="onWrapperClick"
    >
      <component
        v-bind="seg?.props"
        :is="seg?.component"
        v-for="(seg, index) in segments"
        :key="index"
        :ref="setSegmentRef(index)"
        v-model="model"
        :readonly="readonly"
        @next="focusNext(index)"
        @prev="focusPrev(index)"
        @focus="onFocus"
      />
    </div>
  </on-click-outside>
</template>

<style lang="scss" scoped>
@import '../../../../../assets/scss/shared/brand.module';
@import '../../../../../assets/scss/shared/variables/ds.spacing';
@import '../../../../../assets/scss/shared/variables/ds.radius';

.ds-format-segment-builder__wrapper {
  padding: $space-xs-mobile;
  border-radius: $radius-md-mobile;

  &--selectable {
    &:hover {
      background-color: getColor('grey100');
    }
  }

  &--selected {
    background-color: getColor('grey100');
  }
}
</style>
